import { DesignationBadge, VerifiedCheck } from "@/components/catalog/Badges";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Crown, Film, Lock, Play, Share2, ShieldAlert } from "lucide-react";
import { useBilling } from "@/context/BillingContext";
import { useGuest } from "@/context/GuestContext";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MyListButton } from "@/components/catalog/MyListButton";
import { TrailerModal } from "@/components/catalog/TrailerModal";
import { Rail } from "@/components/catalog/Rail";
import { EmptyState, Spinner } from "@/components/catalog/TitleGrid";
import { useProfiles } from "@/context/ProfileContext";
import { api, formatApiError } from "@/lib/api";
import { fmtDuration, fmtRemaining, fmtRuntime, TYPE_LABEL } from "@/lib/format";
import { resolveResume, watchPath } from "@/lib/play";

const EpisodeCard = ({ slug, ep, entry, onPlay }) => {
  const pct = entry ? Math.min(100, Math.round((entry.position / (entry.duration || 1)) * 100)) : 0;
  return (
    <button data-testid={`episode-card-${ep.id}`} onClick={onPlay} className="group flex w-full flex-col sm:flex-row gap-4 rounded-xl border border-[#D4AF37]/15 bg-[#0B0B0D] p-3 text-left transition-[border-color,background-color] hover:border-[#FFD86A]/60 hover:bg-[#0E0D0A] active:scale-[0.99]">
      <div className="relative aspect-video w-full sm:w-[260px] flex-none overflow-hidden rounded-xl">
        <img src={ep.thumbnail_url} alt={ep.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" style={{ objectPosition: `${(ep.number * 29) % 100}% center` }} />
        <span className="absolute inset-0 m-auto grid h-12 w-12 place-items-center rounded-full border border-[#FFD86A]/70 bg-black/55 text-[#FFD86A] opacity-90 group-hover:scale-110 transition-transform">{ep.locked ? <Lock data-testid={`episode-lock-${ep.id}`} className="h-5 w-5" /> : <Play className="h-5 w-5 fill-current ml-0.5" />}</span>
        {ep.access_tier === "free" && <span data-testid={`episode-free-${ep.id}`} className="absolute left-2 top-2 rounded-full bg-[#050505]/80 px-2 py-0.5 text-[0.6rem] font-bold tracking-[0.18em] text-[#7CF7FF]">FREE</span>}
        {pct > 0 && (
          <div className="absolute inset-x-0 bottom-0 h-1 bg-white/20"><div data-testid={`episode-progress-${ep.id}`} className="h-full bg-gradient-to-r from-[#D4AF37] to-[#FFD86A]" style={{ width: `${pct}%` }} /></div>
        )}
      </div>
      <div className="flex-1 min-w-0 py-1">
        <div className="flex items-baseline justify-between gap-3">
          <h4 className="font-display text-base sm:text-lg text-white"><span className="text-[#D4AF37] mr-2">{ep.number}.</span>{ep.title}</h4>
          <span data-testid={`episode-duration-${ep.id}`} className="flex-none text-sm text-[#F5C451]">{fmtDuration(ep.duration_sec)}</span>
        </div>
        <p className="mt-2 text-sm leading-relaxed text-[#8B8B8B] line-clamp-3">{ep.synopsis}</p>
        {entry && !entry.completed && entry.position > 0 && <p className="mt-2 text-xs text-[#F5C451]">{fmtRemaining(entry.duration - entry.position)}</p>}
        {entry?.completed && <p className="mt-2 text-xs text-[#8B8B8B]">Watched</p>}
      </div>
    </button>
  );
};

export default function Detail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { activeProfile } = useProfiles();
  const [title, setTitle] = useState(null);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState({ entries: [], resume: {} });
  const [similar, setSimilar] = useState([]);
  const [season, setSeason] = useState("1");
  const [trailer, setTrailer] = useState(false);
  const { openUpgrade } = useBilling();
  const { isGuest } = useGuest();

  useEffect(() => {
    setTitle(null);
    setError("");
    window.scrollTo(0, 0);
    api.get(`/catalog/titles/${slug}`).then((r) => { setTitle(r.data); setSeason(String(r.data.seasons?.[0]?.number || 1)); }).catch((e) => setError(formatApiError(e)));
    resolveResume(slug).then(setProgress);
    api.get(`/catalog/titles/${slug}/similar`).then((r) => setSimilar(r.data)).catch(() => setSimilar([]));
  }, [slug, activeProfile?.id]);

  const entries = useMemo(() => Object.fromEntries(progress.entries.map((e) => [e.episode_id || "_", e])), [progress]);

  useEffect(() => {
    const ep = progress.resume?.episode_id;
    const s = title?.seasons?.find((x) => x.episodes.some((e) => e.id === ep));
    if (s) setSeason(String(s.number));
  }, [progress, title]);

  if (error) return <div className="mx-auto max-w-3xl px-6 pt-36"><EmptyState icon={ShieldAlert} title="Unavailable" text={error} testId="detail-error" /></div>;
  if (!title) return <div className="pt-24"><Spinner testId="detail-loading" /></div>;

  const hasProgress = progress.entries.length > 0 && (progress.resume.position > 0 || title.type === "series");
  const resumeEp = title.seasons.flatMap((s) => s.episodes.map((e) => ({ ...e, season: s.number }))).find((e) => e.id === progress.resume.episode_id);
  const watchLabel = !hasProgress ? "Watch Now" : resumeEp ? `Resume S${resumeEp.season}:E${resumeEp.number}` : "Resume";
  const currentSeason = title.seasons.find((s) => String(s.number) === season);

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) return await navigator.share({ title: title.title, text: title.tagline, url });
    } catch (e) {
      if (e?.name === "AbortError") return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch {
      toast("Share this link", { description: url });
    }
  };

  return (
    <div data-testid="detail-page">
      {title.status && title.status !== "published" && (
        <div data-testid="detail-preview-banner" className="fixed top-20 md:top-24 left-1/2 z-40 -translate-x-1/2 rounded-full border border-[#F5C451]/60 bg-[#050505]/90 px-5 py-2 text-xs font-bold tracking-[0.2em] text-[#F5C451] backdrop-blur">
          PREVIEW · {title.status.toUpperCase()} · NOT VISIBLE TO VIEWERS
        </div>
      )}
      <section className="relative min-h-[620px] overflow-hidden">
        <img src={title.backdrop_url} alt="" className="absolute inset-0 h-full w-full object-cover object-[70%_center]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/80 to-[#050505]/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/30 to-black/50" />
        <div className="relative z-10 mx-auto flex max-w-[1680px] flex-col md:flex-row gap-8 lg:gap-14 px-4 sm:px-6 lg:px-10 pt-28 md:pt-40 pb-14">
          <img src={title.poster_url} alt={title.title} className="hidden md:block w-[240px] lg:w-[290px] aspect-[2/3] flex-none rounded-2xl object-cover border border-[#D4AF37]/40 shadow-[0_30px_70px_rgba(0,0,0,0.85),0_0_40px_rgba(212,175,55,0.15)]" />
          <div className="max-w-2xl space-y-5">
            <p className="eyebrow flex flex-wrap items-center gap-2">{title.designation ? <DesignationBadge value={title.designation} size="lg" testId="detail-designation" /> : <Crown className="h-4 w-4" />} {TYPE_LABEL[title.type]}{title.creator_verified && <span data-testid="detail-verified" className="flex items-center gap-1 normal-case tracking-normal text-[#FFD86A]"><VerifiedCheck /> Verified creator</span>}</p>
            <h1 data-testid="detail-title" className="font-display font-black text-4xl sm:text-5xl lg:text-6xl gold-text leading-tight" style={{ fontWeight: 900 }}>{title.title}</h1>
            {title.tagline && <p className="font-display text-base md:text-lg tracking-[0.12em] text-[#F5C451]">{title.tagline}</p>}
            <div className="flex flex-wrap items-center gap-3 text-sm text-white/85" data-testid="detail-meta">
              <span>{title.year}</span>
              {title.type === "series" ? <span>{title.seasons.length} Season{title.seasons.length > 1 ? "s" : ""}</span> : <span>{fmtRuntime(title.runtime_min)}</span>}
              <span className="chip">{title.maturity_rating}</span>
              <span className="chip">{title.quality}</span>
              <span className="chip">{title.audio}</span>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="detail-genres">
              {title.genres.map((g) => <span key={g} className="rounded-full border border-white/15 px-3 py-1 text-xs text-white/75">{g}</span>)}
            </div>
            <p data-testid="detail-description" className="text-sm sm:text-base leading-relaxed text-white/75">{title.description}</p>
            <div className="flex flex-wrap gap-3 pt-1">
              <button data-testid="detail-watch-btn" onClick={() => (title.locked ? openUpgrade(title) : navigate(watchPath(title.slug, progress.resume.episode_id)))} className="btn-gold px-7 sm:px-9 py-3 text-sm sm:text-base">
                {title.locked ? <><Lock className="h-5 w-5" /> {isGuest ? "Sign in to watch" : "Upgrade to watch"}</> : <><Play className="h-5 w-5 fill-current" /> {watchLabel}</>}
              </button>
              <button data-testid="detail-trailer-btn" onClick={() => setTrailer(true)} className="btn-ghost-gold px-5 sm:px-6 py-3 text-sm sm:text-base"><Film className="h-5 w-5 text-[#FFD86A]" /> Trailer</button>
              <MyListButton title={title} testId="detail-my-list-btn" />
              <button data-testid="detail-share-btn" onClick={share} className="btn-ghost-gold px-5 sm:px-6 py-3 text-sm sm:text-base"><Share2 className="h-5 w-5 text-[#FFD86A]" /> Share</button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 pt-3 text-sm">
              <div data-testid="detail-cast"><p className="eyebrow !text-[0.68rem] mb-1.5">Starring</p><p className="text-white/80">{title.cast.map((c) => c.name).join(", ")}</p></div>
              <div data-testid="detail-director"><p className="eyebrow !text-[0.68rem] mb-1.5">Director</p><p className="text-white/80">{title.director}</p></div>
            </div>
          </div>
        </div>
      </section>

      {title.type === "series" && currentSeason && (
        <section className="mx-auto max-w-[1680px] px-4 sm:px-6 lg:px-10 pb-6" data-testid="episodes-section">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <h2 className="font-display text-base md:text-lg uppercase tracking-[0.28em] gold-text">Episodes</h2>
            <Select value={season} onValueChange={setSeason}>
              <SelectTrigger data-testid="season-select" className="w-[280px] rounded-full border-[#D4AF37]/40 bg-[#0B0B0D] text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0B0B0D] border-[#D4AF37]/30 text-white">
                {title.seasons.map((s) => (
                  <SelectItem key={s.number} value={String(s.number)} data-testid={`season-option-${s.number}`}>{s.title || `Season ${s.number}`}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="mb-5 text-sm text-[#8B8B8B]" data-testid="season-summary">{currentSeason.title} · {currentSeason.episodes.length} episodes</p>
          <div className="grid gap-4 lg:grid-cols-2">
            {currentSeason.episodes.map((ep) => (
              <EpisodeCard key={ep.id} slug={title.slug} ep={ep} entry={entries[ep.id]} onPlay={() => (ep.locked ? openUpgrade(title) : navigate(watchPath(title.slug, ep.id)))} />
            ))}
          </div>
        </section>
      )}

      {similar.length > 0 && <div className="pt-8"><Rail title="More Like This" items={similar} testId="rail-more-like-this" /></div>}
      <TrailerModal title={title} open={trailer} onOpenChange={setTrailer} />
    </div>
  );
}
